/**
 * Password rules for the sign-up form's live checklist. Mirrors
 * server/src/lib/passwordPolicy.ts, which has the final say — keep the two in
 * step.
 */

export type PasswordRole = "user" | "admin";

export const PASSWORD_MIN = { user: 10, admin: 12 } as const;
export const PASSWORD_MAX = 128;

export type PasswordProblem =
  | "tooShort"
  | "tooLong"
  | "needsLetter"
  | "needsNumber"
  | "containsPersonal"
  | "tooCommon";

/** Words that, with a few digits or symbols around them, make a guessable password. */
const COMMON_WORDS = new Set([
  "password", "passw", "pass", "qwerty", "qwertyuiop", "asdfgh", "asdfghjkl", "zxcvbnm", "azerty",
  "iloveyou", "welcome", "letmein", "admin", "administrator", "login", "master", "secret", "changeme",
  "football", "baseball", "soccer", "monkey", "dragon", "sunshine", "princess", "shadow", "superman",
  "batman", "trustno", "starwars", "hello", "freedom", "whatever", "computer", "internet", "summer",
  "winter", "spring", "autumn", "default", "test", "guest", "user", "parola", "parolata",
  "aistyle", "mediastyle", "media", "style", "sofia", "bulgaria", "razgrad",
]);

/** Keyboard and digit runs that are guessed first whatever surrounds them. */
const COMMON_RUNS = [
  "1234567890", "0987654321", "123456789", "987654321", "12345678", "1q2w3e4r", "q1w2e3r4",
  "qwertyuiop", "asdfghjkl", "zxcvbnm", "11111111", "00000000", "abcdefgh",
];

const lettersOf = (s: string) => s.toLowerCase().replace(/[^\p{L}]/gu, "");

/** The parts of a name or email someone might reuse ("ivan", "petrov", "ivan.petrov"). */
function personalTokens(email?: string, fullName?: string): string[] {
  const local = (email ?? "").split("@")[0] ?? "";
  return [local, ...local.split(/[^\p{L}\p{N}]+/u), ...(fullName ?? "").split(/\s+/)]
    .map((t) => t.toLowerCase().trim())
    .filter((t) => t.length >= 4);
}

/**
 * The first rule the password breaks, or null when it is acceptable. Checked
 * in the order a person would fix them: length, then make-up, then guessability.
 */
export function passwordProblem(
  password: string,
  context: { role?: PasswordRole; email?: string; fullName?: string } = {}
): PasswordProblem | null {
  const min = PASSWORD_MIN[context.role ?? "user"];
  if (password.length < min) return "tooShort";
  if (password.length > PASSWORD_MAX) return "tooLong";
  if (!/\p{L}/u.test(password)) return "needsLetter";
  if (!/\p{N}/u.test(password)) return "needsNumber";

  const lower = password.toLowerCase();
  if (personalTokens(context.email, context.fullName).some((t) => lower.includes(t))) {
    return "containsPersonal";
  }

  // A common word padded with digits or symbols ("Password2026!"), one
  // character repeated, or a keyboard / digit run making up most of it.
  const letters = lettersOf(password);
  if (COMMON_WORDS.has(letters) && password.length - letters.length <= 8) return "tooCommon";
  if (new Set(lower).size <= 3) return "tooCommon";
  if (COMMON_RUNS.some((run) => lower.includes(run) && run.length >= lower.length - 4)) return "tooCommon";

  return null;
}
